import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, MessageSquareReply } from "lucide-react";

const Stars = ({ value, size = 14 }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} style={{ width: size, height: size }} className={n <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300 dark:text-slate-600"} />
    ))}
  </span>
);

export default function Reviews() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [text, setText] = useState("");

  const load = () => api.get("/my-reviews").then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const startReply = (rv) => { setEditing(rv.id); setText(rv.reply || ""); };
  const saveReply = async (rv) => {
    try {
      await api.post(`/reviews/${rv.id}/reply`, { reply: text });
      toast.success("Resposta publicada");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (list === null) return <div className="h-40 animate-pulse bg-secondary rounded-2xl" />;

  const avg = list.length ? (list.reduce((s, r) => s + (r.rating || 0), 0) / list.length).toFixed(1) : 0;

  return (
    <div data-testid="therapist-reviews">
      <PageHeader title="Avaliações" subtitle="Veja o que seus clientes dizem e responda publicamente." />

      {list.length > 0 && (
        <Card className="p-5 mb-5 flex items-center gap-4" data-testid="reviews-summary">
          <div className="text-center">
            <div className="font-heading text-4xl font-semibold text-primary">{avg}</div>
            <Stars value={avg} />
          </div>
          <div className="text-sm text-muted-foreground">Baseado em {list.length} avaliação(ões) de clientes.</div>
        </Card>
      )}

      {list.length === 0 ? (
        <EmptyState icon={Star} title="Sem avaliações ainda" desc="Assim que seus clientes avaliarem os atendimentos, elas aparecerão aqui." />
      ) : (
        <div className="space-y-3">
          {list.map((rv) => (
            <Card key={rv.id} className="p-5" data-testid={`review-item-${rv.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">{rv.client_name}</span>
                <Stars value={rv.rating} />
              </div>
              {rv.comment && <p className="text-sm text-muted-foreground mt-2">{rv.comment}</p>}

              {rv.reply && editing !== rv.id && (
                <div className="mt-3 pl-3 border-l-2 border-primary/40">
                  <p className="text-xs font-medium text-primary">Sua resposta</p>
                  <p className="text-sm text-muted-foreground">{rv.reply}</p>
                </div>
              )}

              {editing === rv.id ? (
                <div className="mt-3 space-y-2">
                  <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Escreva uma resposta pública..." data-testid={`reply-input-${rv.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" className="rounded-full" onClick={() => saveReply(rv)} data-testid={`save-reply-${rv.id}`}>Publicar resposta</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" className="rounded-full mt-3" onClick={() => startReply(rv)} data-testid={`reply-btn-${rv.id}`}>
                  <MessageSquareReply className="w-4 h-4 mr-1" />{rv.reply ? "Editar resposta" : "Responder"}
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
